import React, { useState, useEffect } from "react";
import SEO from "../seo";
const ChooseRightApp = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "functionality-gap",
        "sales-growth",
        "store-management",
        "app-reviews",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Choose the Right App | xPage Help Center"
        description="Learn how to choose the best apps for your Xpage store. Consider functionality gaps, sales growth, store management, and app reviews to select the most effective tools for your business."
        keywords="Xpage apps, choose apps, best apps for ecommerce, store management apps, app reviews, app selection guide"
        canonical="https://help.xpage.ai/help/apps/Choose%20the%20Right%20App"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Choose the Right App 
          </h1>
          <p className="text-sm text-systemLabel">
            Xpage offers a broad selection of apps to enhance your store,
            streamline operations, and boost sales. To select the most effective
            app for your business, consider these key points:
          </p>
        </header>

        {/* Functionality Gap Section */}
        <section id="functionality-gap" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            1. Does it fill a functionality gap?
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Identify areas where your store could benefit from additional
                features beyond Xpage's core capabilities:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Efficiency improvements
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Are there tasks you could complete faster with automation?
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Missing features
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Are there tools or functionalities that could enhance your
                      store's performance?
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Sales Growth Section */}
        <section id="sales-growth" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            2. Can it help drive sales growth?
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Choose apps that directly support revenue and customer
                engagement:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Traffic generation
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Do you need help with SEO, ads, or attracting new
                      visitors?
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Promotions & campaigns
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Would discounts, bundles, or loyalty features encourage
                      more purchases?
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Store Management Section */}
        <section id="store-management" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            3. Will it simplify store management?
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Look for apps that reduce manual work and save time:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Order management
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Bulk shipping labels, automatic inventory updates, and
                      workflow automation.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Data insights
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Tools to track sales, monitor trends, and make data-driven
                      decisions in real time.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* App Reviews Section */}
        <section id="app-reviews" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            App Reviews
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Evaluating reviews and ratings helps you make informed choices:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Star Ratings
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Quick overview of app satisfaction, from one to five
                      stars.
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Genuine Feedback
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Only active Xpage merchants who have installed or recently
                      uninstalled the app can leave reviews.
                    </td>
                  </tr>
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        Review Management
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Each merchant can leave one review per app and update it
                      if necessary, ensuring feedback stays current.
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm text-systemLabel">
                Merchant reviews are a valuable resource for understanding an
                app's real-world performance and whether it suits your store's
                needs.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#functionality-gap"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "functionality-gap"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Functionality Gap
              </a>
            </li>
            <li>
              <a
                href="#sales-growth"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "sales-growth"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Sales Growth
              </a>
            </li>
            <li>
              <a
                href="#store-management"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "store-management"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Store Management
              </a>
            </li>
            <li>
              <a
                href="#app-reviews"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "app-reviews"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                App Reviews
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChooseRightApp;
